/*
  Copyright (c) 2018-present, Accuracy.
  All rights reserved.
*/
import {createStyles, StyleRules, Theme} from "@material-ui/core";
import makeStyles from '@material-ui/core/styles/makeStyles';
import classNames from 'classnames';
import React, {memo} from 'react';
import {useTranslation} from "react-i18next";
import {asset} from "../../utils/asset";

//----------------------------------------------------------------------------------------------------------------------
//- Data
//----------------------------------------------------------------------------------------------------------------------
// Brands, print/photo suppliers and fellow photographers Pierre wants to point
// visitors to. Carried over from the legacy site's "liens.html" page — names
// and captions are proper nouns/job titles, kept in French regardless of the
// active language, same convention as the venue names in Expos.tsx.
type LinkEntry = {
  name: string;
  caption?: string;
  href: string;
  img: string;
};

const LINKS: LinkEntry[] = [
  {name: 'Escourbiac', caption: 'Imprimeur', href: 'http://www.escourbiac.com/index.php', img: 'Escourbiac.jpg'},
  {name: 'Prophot', caption: 'Magasin photo & vidéo — Toulouse', href: 'https://www.materiel-photo-pro.com/magasin-photo-video-toulouse.html', img: 'Prophot.jpg'},
  {name: 'Nikon', href: 'https://www.nikon.fr/fr_FR', img: 'nikon.jpg'},
  {name: 'Epson', href: 'https://www.epson.fr/', img: 'epson.jpg'},
  {name: 'Hahnemühle', caption: 'Fine Art', href: 'https://www.hahnemuehle.com/fr/digital-fineart.html', img: 'Hahnemuhle.jpg'},
  {name: 'Jean-Luc Sarda', caption: 'Photographe', href: 'http://www.sarda-photo.com/index.php', img: 'sarda.png'},
  {name: 'Yvon Monet', caption: 'Photographe auteur', href: 'https://www.yvon-monet.fr/index.php', img: 'Yvon_Monet.jpg'},
];

//----------------------------------------------------------------------------------------------------------------------
//- Styles
//----------------------------------------------------------------------------------------------------------------------
export type LinksClassKey = 'root' | 'heading' | 'intro' | 'grid' | 'card' | 'logoBox' | 'logo' | 'name' | 'caption';

const useStyles = makeStyles<Theme, {}, LinksClassKey>(
  (): StyleRules<LinksClassKey> =>
    createStyles({
      root: {
        padding: 'clamp(48px, 10vh, 120px) clamp(20px, 4vw, 56px) clamp(60px, 10vh, 120px)'
      },
      heading: {
        margin: '0 0 clamp(20px, 3vh, 32px)',
        fontFamily: 'var(--font-display)',
        fontSize: 'clamp(30px, 4vw, 56px)',
        letterSpacing: '-.015em'
      },
      intro: {
        margin: '0 0 clamp(28px, 5vh, 48px)',
        maxWidth: '62ch',
        fontSize: '15px',
        lineHeight: 1.85,
        color: 'var(--color-text-body)'
      },
      grid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))',
        gap: 'clamp(16px, 2.5vw, 28px)'
      },
      card: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: '10px',
        padding: '18px 14px',
        border: '1px solid var(--color-rule)',
        textDecoration: 'none',
        transition: 'border-color .2s, background .2s',
        '&:hover': {
          borderColor: 'rgba(255,255,255,.35)',
          background: 'var(--color-hover-tint)'
        }
      },
      logoBox: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: '100%',
        height: '72px',
        // Source logos come from a legacy site with mismatched backgrounds
        // (solid yellow, solid black, transparent...) — a fixed light plaque
        // keeps every one of them legible against the page's dark background.
        background: '#f4f2ee'
      },
      logo: {
        maxWidth: '100%',
        maxHeight: '100%',
        objectFit: 'contain'
      },
      name: {
        margin: 0,
        textAlign: 'center',
        fontSize: '12.5px',
        letterSpacing: '.08em',
        textTransform: 'uppercase',
        color: 'var(--color-text)'
      },
      caption: {
        margin: 0,
        textAlign: 'center',
        fontSize: '11px',
        letterSpacing: '.04em',
        color: 'var(--color-text-faint)'
      }
    }),
  {
    name: 'Links'
  });

//----------------------------------------------------------------------------------------------------------------------
//- Component
//----------------------------------------------------------------------------------------------------------------------
export type LinksProps = {
  className?: string;
};

export default memo(({className}: LinksProps) => {
  // hooks
  const classes = useStyles({});
  const {t} = useTranslation('common');

  return (
    <div className={classNames(classes.root, className)}>
      <h2 className={classes.heading}>{t('topBarMenu.links')}</h2>
      <p className={classes.intro}>{t('links.description')}</p>

      <div className={classes.grid}>
        {LINKS.map((link) => (
          <a key={link.name} className={classes.card} href={link.href} target="_blank" rel="noopener noreferrer">
            <div className={classes.logoBox}>
              <img className={classes.logo} src={asset(`files/img/liens/${link.img}`)} alt={link.name}/>
            </div>
            <p className={classes.name}>{link.name}</p>
            {link.caption && <p className={classes.caption}>{link.caption}</p>}
          </a>
        ))}
      </div>
    </div>
  );
});
